import { useParams, useNavigate } from "react-router-dom";
import { useGetOrdersDetails, useGetOrdersApproved, useDeleteOrder } from "../backend/hooks";
import { toast } from "react-toastify";
import { useState } from "react";
import Swal from "sweetalert2";
import { Skeleton, TableRowsSkeleton } from '../common/skeleton';

export default function OrderDetails() {
    const { id } = useParams();
    const { data: order, isLoading, error, refetch } = useGetOrdersDetails(id);
    const { mutate: approveOrder, isPending: isApproving } = useGetOrdersApproved();
    const [isStatusUpdating, setIsStatusUpdating] = useState(false);
    const { mutate: deleteOrder, isPending: isDeleting } = useDeleteOrder();
    const navigate = useNavigate();

    const handleDeleteOrder = () => {
        Swal.fire({
            title: `Delete order #${id}?`,
            text: "This order, its items and payment screenshot will be permanently removed.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes, delete it",
        }).then((result) => {
            if (!result.isConfirmed) return;
            deleteOrder(id, {
                onSuccess: () => {
                    toast.success(`Order #${id} deleted`);
                    navigate("/dashboard/Orders");
                },
                onError: (err) => toast.error(err.message),
            });
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const getOrderTotal = (items) => {
        if (!items || !items.length) return 0;
        return items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0);
    };

    const getItemsArray = (orderData) => {
        if (orderData.items && orderData.items.length) return orderData.items;
        if (orderData.items_json) {
            try {
                return JSON.parse(orderData.items_json);
            } catch (e) {
                return [];
            }
        }
        return [];
    };

    const handleApproveOrder = async () => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This will mark the order as approved. This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#0a7e3a",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, approve it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            setIsStatusUpdating(true);
            approveOrder(id, {
                onSuccess: () => {
                    toast.success("Order approved!");
                    refetch();
                    setIsStatusUpdating(false);
                },
                onError: (err) => {
                    toast.error(err.message || "Failed to approve order");
                    setIsStatusUpdating(false);
                },
            });
        }
    };

    if (isLoading) {
        return (
            <main className="min-h-screen bg-surface">
                <div className="max-w-7xl mx-auto space-y-6 p-4 md:p-8">
                    {/* Header card */}
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white p-5 md:p-8 rounded-2xl shadow-sm">
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <Skeleton className="h-5 w-28 rounded-full" />
                                <Skeleton className="h-4 w-24 rounded" />
                            </div>
                            <Skeleton className="h-10 w-40 rounded-lg" />
                            <Skeleton className="h-6 w-32 rounded-lg" />
                        </div>
                        <Skeleton className="h-11 w-40 rounded-xl" />
                    </div>

                    {/* Customer / payment / shipping cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {Array.from({ length: 3 }).map((_, idx) => (
                            <div key={idx} className="bg-white p-5 rounded-2xl space-y-4">
                                <Skeleton className="h-5 w-40 rounded" />
                                <Skeleton className="h-3 w-24 rounded" />
                                <Skeleton className="h-4 w-3/4 rounded" />
                                <Skeleton className="h-3 w-20 rounded" />
                                <Skeleton className="h-4 w-1/2 rounded" />
                                <Skeleton className="h-4 w-5/6 rounded" />
                            </div>
                        ))}
                    </div>

                    {/* Items table */}
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                        <div className="p-5 border-b border-slate-100">
                            <Skeleton className="h-5 w-36 rounded" />
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[500px]">
                                <tbody>
                                    <TableRowsSkeleton columns={4} rows={3} withImage cellClassName="px-5 py-4" />
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    if (error || !order) {
        return (
            <main className="min-h-screen bg-surface p-8">
                <div className="bg-error/10 text-error p-4 rounded-xl text-center">
                    Failed to load order details. Please try again later.
                </div>
            </main>
        );
    }

    const items = getItemsArray(order);
    const subtotalAmount = getOrderTotal(items);
    const shippingAmount = Number(order.shipping_amount) || 0;
    const discountAmount = Number(order.discount_amount) || 0;
    const totalAmount = Math.max(subtotalAmount - discountAmount, 0) + shippingAmount;
    const paidAmount = totalAmount * 0.5;
    const balanceDue = totalAmount - paidAmount;

    const isComplete = order.order_complete === 1;
    const statusDisplay = isComplete
        ? { label: "Approved", color: "bg-green-100 text-green-800", dot: "bg-green-500" }
        : { label: "Pending", color: "bg-amber-100 text-amber-800", dot: "bg-amber-500" };

    const paymentScreenshotUrl = order.payment_screen_short
        ? `${process.env.REACT_APP_BE_URL}/${order.payment_screen_short.replace(/\\/g, '/')}`
        : null;

    const isImage = (url) => {
        if (!url) return false;
        const ext = url.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
    };

    return (
        <main className="min-h-screen bg-surface">
            <div className="max-w-7xl mx-auto space-y-6 p-4 md:p-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white p-5 md:p-8 rounded-2xl shadow-sm">
                    <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="px-3 py-1 bg-secondary-fixed text-on-secondary-fixed text-[10px] font-bold uppercase tracking-widest rounded-full">
                                Order Details
                            </span>
                            <span className="text-slate-400 font-medium">•</span>
                            <span className="text-sm text-slate-500 font-medium">
                                {formatDate(order.created_at)}
                            </span>
                        </div>
                        <h1 className="text-3xl md:text-4xl font-extrabold text-sky-900 tracking-tight break-all">
                            #{order.id}
                        </h1>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className={`flex items-center gap-1.5 px-3 py-1 ${statusDisplay.color} rounded-lg text-xs font-bold whitespace-nowrap`}>
                                <span className={`w-2 h-2 rounded-full ${statusDisplay.dot}`}></span>
                                {statusDisplay.label}
                            </span>
                            <span className="text-sm text-slate-400">Updated {formatDate(order.created_at)}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {!isComplete && (
                            <button
                                onClick={handleApproveOrder}
                                disabled={isApproving || isStatusUpdating}
                                className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white font-bold text-sm rounded-xl shadow-md hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isApproving || isStatusUpdating ? (
                                    <>
                                        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined text-lg">check_circle</span>
                                        Approve Order
                                    </>
                                )}
                            </button>
                        )}
                        {isComplete && (
                            <span className="text-sm font-semibold text-green-600">Order Approved</span>
                        )}
                        <button
                            onClick={handleDeleteOrder}
                            disabled={isDeleting}
                            className="flex items-center gap-2 px-5 py-2.5 bg-error/10 text-error font-bold text-sm rounded-xl hover:bg-error hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-lg">delete</span>
                            {isDeleting ? "Deleting..." : "Delete Order"}
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {/* Customer Details – now includes email */}
                    <div className="bg-white p-5 rounded-2xl space-y-3">
                        <div className="flex items-center gap-3 text-primary">
                            <span className="material-symbols-outlined">person</span>
                            <h3 className="font-bold text-slate-900">Customer Details</h3>
                        </div>
                        <div className="space-y-2">
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Primary Contact</p>
                                <p className="text-sm font-semibold text-slate-900 break-words">{order.name}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Phone</p>
                                <p className="text-xs font-medium text-slate-900">{order.phone}</p>
                            </div>
                            {/* ✅ New: Email field */}
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Email</p>
                                <p className="text-xs font-medium text-slate-900 break-words">{order.email || "N/A"}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Shipping Address</p>
                                <p className="text-xs leading-relaxed text-slate-600 break-words">
                                    {order.address}<br />
                                    {order.city}, {order.postal_code || ""}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Payment Status card – unchanged */}
                    <div className="bg-white p-5 rounded-2xl space-y-3">
                        <div className="flex items-center gap-3 text-tertiary">
                            <span className="material-symbols-outlined">payments</span>
                            <h3 className="font-bold text-slate-900">Payment Status</h3>
                        </div>
                        <div className="bg-tertiary-fixed p-3 rounded-xl border border-tertiary/10">
                            <div className="flex items-center justify-between mb-1">
                                <p className="text-[10px] font-bold text-on-tertiary-fixed uppercase">Payment Progress</p>
                                <p className="text-[10px] font-bold text-on-tertiary-fixed">50%</p>
                            </div>
                            <div className="w-full bg-white/50 h-1.5 rounded-full overflow-hidden">
                                <div className="bg-tertiary h-full w-1/2"></div>
                            </div>
                            <p className="mt-2 text-xs font-bold text-on-tertiary-fixed-variant">
                                50% Advance Payment Received
                            </p>
                        </div>
                        <div className="space-y-3 pt-1">
                            <div className="flex justify-between items-center">
                                <p className="text-xs text-slate-500 font-medium">Method</p>
                                <p className="text-xs font-bold text-slate-900">JazzCash / Bank Transfer</p>
                            </div>
                            {paymentScreenshotUrl && isImage(paymentScreenshotUrl) && (
                                <a
                                    href={paymentScreenshotUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex justify-between items-center p-2 bg-slate-50 rounded-lg group hover:bg-slate-100 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-slate-400 text-lg">image</span>
                                        <span className="text-[10px] font-bold text-slate-600 break-all">Payment Screenshot</span>
                                    </div>
                                    <span className="material-symbols-outlined text-primary text-sm flex-shrink-0">open_in_new</span>
                                </a>
                            )}
                            {paymentScreenshotUrl && !isImage(paymentScreenshotUrl) && (
                                <a
                                    href={paymentScreenshotUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex justify-between items-center p-2 bg-slate-50 rounded-lg group hover:bg-slate-100 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-slate-400 text-lg">picture_as_pdf</span>
                                        <span className="text-[10px] font-bold text-slate-600 break-all">Payment PDF</span>
                                    </div>
                                    <span className="material-symbols-outlined text-primary text-sm flex-shrink-0">open_in_new</span>
                                </a>
                            )}
                            <div className="grid grid-cols-2 gap-3 pt-2">
                                <div className="p-3 bg-slate-50 rounded-xl">
                                    <p className="text-[10px] text-slate-400 font-bold">TOTAL</p>
                                    <p className="text-sm font-extrabold text-slate-900">Rs {totalAmount.toFixed(2)}</p>
                                </div>
                                <div className="p-3 bg-secondary-fixed/30 rounded-xl">
                                    <p className="text-[10px] text-secondary font-bold">PAID</p>
                                    <p className="text-sm font-extrabold text-secondary">Rs {paidAmount.toFixed(2)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Shipping Address card – unchanged */}
                    <div className="bg-white p-5 rounded-2xl space-y-3">
                        <div className="flex items-center gap-3 text-secondary">
                            <span className="material-symbols-outlined">location_on</span>
                            <h3 className="font-bold text-slate-900">Shipping Address</h3>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Full Address</p>
                            <p className="text-sm leading-relaxed text-slate-700 mt-1 break-words">
                                {order.address}<br />
                                {order.city}, {order.postal_code || ""}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Order Items Table – unchanged */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
                        <h3 className="font-bold text-slate-900">Order Items ({items.length})</h3>
                        <span className="text-xs text-slate-400 font-medium">Managed by Warehouse A</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[500px]">
                            <thead>
                                <tr className="bg-slate-50/50">
                                    <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Product</th>
                                    <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Unit Price</th>
                                    <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Qty</th>
                                    <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((item, idx) => {
                                    const itemTotal = (item.quantity || 0) * (item.unit_price || 0);
                                    return (
                                        <tr key={idx} className="hover:bg-slate-50/30 transition-colors">
                                            <td className="px-5 py-4">
                                                <p className="text-sm font-bold text-slate-900 break-words">{item.product_name}</p>
                                            </td>
                                            <td className="px-4 py-4 text-sm font-semibold text-slate-900 whitespace-nowrap">
                                                Rs {(Number(item.unit_price) || 0).toFixed(2)}
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className="px-3 py-1 bg-slate-100 rounded-lg text-sm font-bold text-slate-700">
                                                    {item.quantity || 0}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 text-sm font-bold text-slate-900 text-right whitespace-nowrap">
                                                Rs {itemTotal.toFixed(2)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="p-5 bg-slate-50/50 flex flex-col md:flex-row justify-between gap-6 border-t border-slate-100">
                        <div className="text-xs text-slate-400 font-medium leading-relaxed italic">
                            All items are checked for clinical safety standards and sanitized prior to shipping. Please review the
                            return policy for sensory hygiene products.
                        </div>
                        <div className="w-full md:w-72 space-y-2">
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-500 font-medium">Subtotal</span>
                                <span className="text-slate-900 font-semibold">Rs {subtotalAmount.toFixed(2)}</span>
                            </div>
                            {discountAmount > 0 && (
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-green-700 font-medium">Coupon{order.coupon_code ? ` (${order.coupon_code})` : ''}</span>
                                    <span className="text-green-700 font-semibold">- Rs {discountAmount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-500 font-medium">Shipping (Insured)</span>
                                <span className="text-slate-900 font-semibold">{shippingAmount === 0 ? 'Free' : `Rs ${shippingAmount.toFixed(2)}`}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-slate-500 font-medium">Applied Tax (Exempt)</span>
                                <span className="text-slate-900 font-semibold">Rs 0.00</span>
                            </div>
                            <div className="pt-3 border-t border-slate-200">
                                <div className="flex justify-between items-center">
                                    <span className="font-extrabold text-base md:text-lg text-slate-900">Grand Total</span>
                                    <span className="font-extrabold text-xl md:text-2xl text-primary">Rs {totalAmount.toFixed(2)}</span>
                                </div>
                                <div className="mt-2 text-right">
                                    <span className="inline-block text-[10px] font-bold text-secondary bg-secondary/20 px-2 py-0.5 rounded-full uppercase tracking-widest">
                                        Balance Due: Rs {balanceDue.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row justify-between gap-2 text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] px-2">
                    <span>Tactile Empathy ERP System</span>
                    <div className="flex gap-4">
                        <span>Generated: {new Date().toLocaleString()}</span>
                        <span>Order ID: {order.id}</span>
                    </div>
                </div>
            </div>
        </main>
    );
}